import { api, unwrap } from "@lumberset/api-client";
import { queryOptions } from "@tanstack/react-query";

/**
 * The reference price book as Staff see it: the book, one item's history,
 * their own proposals and how many still wait. Every key starts with
 * "prices", so a proposal invalidates them all at once.
 */

export const priceCategories = [
  "dimension",
  "studs",
  "treated",
  "timbers",
  "panels",
  "engineered",
  "hardware",
  "other",
] as const;
export type PriceCategory = (typeof priceCategories)[number];

export const categoryLabels: Record<PriceCategory, string> = {
  dimension: "Dimension lumber",
  studs: "Studs",
  treated: "Treated lumber",
  timbers: "Timbers and posts",
  panels: "Panels",
  engineered: "Engineered lumber",
  hardware: "Hardware",
  other: "Other",
};

export type PriceSource =
  | "starter_estimate"
  | "supplier_quote"
  | "import"
  | "staff_proposal"
  | "admin"
  | "market_adjustment";

export const sourceLabels: Record<PriceSource, string> = {
  starter_estimate: "Starter estimate",
  supplier_quote: "Supplier quote",
  import: "Price sheet",
  staff_proposal: "Staff research",
  admin: "Admin",
  market_adjustment: "Market move",
};

export const priceUnits = ["ea", "lf", "bf", "mbf", "sheet", "box"] as const;
export type PriceUnit = (typeof priceUnits)[number];

export const unitLabels: Record<PriceUnit, string> = {
  ea: "Each (ea)",
  lf: "Lineal foot (lf)",
  bf: "Board foot (bf)",
  mbf: "Thousand board feet (mbf)",
  sheet: "Sheet",
  box: "Box",
};

export const priceBookQuery = (filters: { q?: string; category?: PriceCategory }) =>
  queryOptions({
    queryKey: ["prices", "book", { q: filters.q?.trim() || undefined, category: filters.category }],
    queryFn: () =>
      unwrap(
        api.staff.prices.$get({
          query: {
            q: filters.q?.trim() || undefined,
            category: filters.category,
            active: "active",
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });

export type BookPrice = Awaited<
  ReturnType<NonNullable<ReturnType<typeof priceBookQuery>["queryFn"]>>
>["prices"][number];

export const priceHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: ["prices", "history", id],
    queryFn: () => unwrap(api.staff.prices[":id"].history.$get({ param: { id } })),
  });

export type PriceHistory = Awaited<
  ReturnType<NonNullable<ReturnType<typeof priceHistoryQuery>["queryFn"]>>
>;

/** The signed-in Staff member's own proposals, newest first. */
export const myProposalsQuery = queryOptions({
  queryKey: ["prices", "proposals", "mine"],
  queryFn: () => unwrap(api.staff.prices.proposals.$get({ query: { mine: "1" } })),
});

export type MyProposal = Awaited<
  ReturnType<NonNullable<(typeof myProposalsQuery)["queryFn"]>>
>["proposals"][number];

/** How many of their proposals still wait for an Admin. */
export const myPendingQuery = queryOptions({
  queryKey: ["prices", "pending-count"],
  queryFn: () => unwrap(api.staff.prices["pending-count"].$get()),
  refetchInterval: 60_000,
});

/** How the matcher reads a description, and whether a code is taken, while a form is filled in. */
export const itemCheckQuery = (input: { description: string; unit: PriceUnit; code?: string }) =>
  queryOptions({
    queryKey: ["prices", "check", input],
    queryFn: () =>
      unwrap(
        api.staff.prices.check.$post({
          json: {
            description: input.description,
            unit: input.unit,
            code: input.code?.trim() || undefined,
          },
        }),
      ),
    enabled: input.description.trim().length > 0,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
